import { useState } from 'react'
import type { TeamInvitation, User } from '../../types/supabase'

interface TeamMembersListProps {
  members: User[]
  invitations: TeamInvitation[]
  onResendInvite?: (invitationId: string) => void
  resendingInvitationId?: string | null
}

const roleLabelMap: Record<User['role'], string> = {
  admin: 'Admin',
  manager: 'Manager',
  employee: 'Employee',
  billing: 'Billing'
}

const invitationBadgeColor: Record<TeamInvitation['status'], string> = {
  pending: 'bg-gray-100 text-gray-700',
  accepted: 'bg-green-100 text-green-700',
  declined: 'bg-red-100 text-red-700',
  expired: 'bg-yellow-100 text-yellow-700'
}

const formatDate = (value?: string | null) => {
  if (!value) return '—'
  return new Date(value).toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric'
  })
}

export const TeamMembersList = ({
  members,
  invitations,
  onResendInvite,
  resendingInvitationId
}: TeamMembersListProps) => {
  const [copiedInviteId, setCopiedInviteId] = useState<string | null>(null)

  const visibleInvitations = invitations.filter(invitation => invitation.status !== 'accepted')

  const handleCopyInvite = async (invitation: TeamInvitation) => {
    if (typeof navigator === 'undefined' || !navigator.clipboard) {
      return
    }

    const origin = typeof window !== 'undefined' ? window.location.origin : ''
    const inviteUrl = `${origin}/accept-invite?token=${invitation.token}`

    try {
      await navigator.clipboard.writeText(inviteUrl)
      setCopiedInviteId(invitation.id)
      setTimeout(() => setCopiedInviteId(null), 2000)
    } catch (error) {
      console.error('TeamMembersList: failed to copy invite link', error)
    }
  }

  return (
    <div className="space-y-6">
      {members.length > 0 && (
        <div className="rounded-3xl border border-gray-200 bg-white p-6">
          <div className="mb-4 flex items-center justify-between">
            <h2 className="text-lg font-semibold text-gray-900">Active members</h2>
            <span className="text-sm text-gray-500">{members.length} total</span>
          </div>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">Name</th>
                  <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">Email</th>
                  <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">Role</th>
                  <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 bg-white">
                {members.map((member) => (
                  <tr key={member.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                      {member.full_name || 'Unnamed'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {member.email}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {roleLabelMap[member.role]}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {member.is_active ? (
                        <span className="inline-flex items-center rounded-full bg-green-100 px-3 py-1 text-xs font-medium text-green-700">
                          Active
                        </span>
                      ) : (
                        <span className="inline-flex items-center rounded-full bg-gray-100 px-3 py-1 text-xs font-medium text-gray-600">
                          Inactive
                        </span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {visibleInvitations.length > 0 && (
        <div className="rounded-3xl border border-gray-200 bg-white p-6">
          <div className="mb-4 flex items-center justify-between">
            <h2 className="text-lg font-semibold text-gray-900">Invitations</h2>
            <span className="text-sm text-gray-500">{visibleInvitations.length} outstanding</span>
          </div>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">Name</th>
                  <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">Email</th>
                  <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">Role</th>
                  <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">Status</th>
                  <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">Last sent</th>
                  <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">Expires</th>
                  <th className="px-6 py-3 text-right text-xs font-medium uppercase tracking-wider text-gray-500">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 bg-white">
                {visibleInvitations.map((invitation) => (
                  <tr key={invitation.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                      {invitation.full_name}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {invitation.email}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 capitalize">
                      {invitation.role}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      <span
                        className={`inline-flex items-center rounded-full px-3 py-1 text-xs font-medium ${invitationBadgeColor[invitation.status]}`}
                      >
                        {invitation.status}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {formatDate(invitation.last_sent_at)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {formatDate(invitation.expires_at)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-right">
                      <div className="flex items-center justify-end gap-2">
                        <button
                          type="button"
                          onClick={() => handleCopyInvite(invitation)}
                          className="rounded-full border border-gray-200 px-3 py-1 text-xs font-medium text-gray-600 transition-colors hover:bg-gray-100"
                        >
                          {copiedInviteId === invitation.id ? 'Copied!' : 'Copy link'}
                        </button>
                        {onResendInvite && (
                          <button
                            type="button"
                            onClick={() => onResendInvite(invitation.id)}
                            disabled={resendingInvitationId === invitation.id}
                            className="rounded-full border border-gray-900 px-3 py-1 text-xs font-medium text-gray-900 transition-colors hover:bg-gray-900 hover:text-white disabled:cursor-not-allowed disabled:opacity-60"
                          >
                            {resendingInvitationId === invitation.id ? 'Resending…' : 'Resend email'}
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  )
}
